import {
  AutopilotTaskDto,
  TaskCategoryTaskType,
  useInvalidateUseGetAutopilotTasksForCampaign,
} from '@urbanx/agx.activities.client'
import { AuthContext } from '@urbanx/agx.auth.client'
import {
    DocumentTypes,
    UploadedDocument,
} from '@urbanx/agx.campaigns.client'
import { AxiosProgressEvent, GenericAbortSignal } from 'axios'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { uploadFile } from 'services'
interface UseMarkedAsExchangeProps {
    campaignId?: string
    contractId?: string
    mode?: 'agent' | 'solicitor'
    task?: AutopilotTaskDto
}

export const useMarkedAsExchange = ({
  campaignId: providedCampaignId,
  contractId: providedContractId,
  mode,
  task,
}: UseMarkedAsExchangeProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState<string | undefined>()
  const [exchangeLetterFiles, setExchangeLetterFiles] = useState<
    (UploadedDocument | File)[]
  >([])
  const [signedContractFiles, setSignedContractFiles] = useState<
    (UploadedDocument | File)[]
  >([])
  const [isSubmitting, setIsSubmitting] = useState(false)

    const { agencyId, contractId: routeContractId } = useParams()
    const { getAuthToken } = useContext(AuthContext)
    const contractId = providedContractId ?? routeContractId
    const isAgentExchange =
        mode === 'agent' ||
        task?.category?.taskType === TaskCategoryTaskType.AgentExchangeInformation
    const isSolicitorExchange =
        mode === 'solicitor' ||
        task?.category?.taskType === TaskCategoryTaskType.SolicitorExchangeInformation
    const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign()

  const openModal = () => {
    setIsOpen(true)
  }
  const closeModal = () => {
    setIsOpen(false)
  }

  const resetState = useCallback(() => {
    setSelectedOption(undefined)
    setExchangeLetterFiles([])
    setSignedContractFiles([])
    setIsSubmitting(false)
  }, [])

  const buildRenamedFormData = useCallback(
    (formData: FormData) => {
      const file = formData.get('File')
      if (!file || typeof File === 'undefined' || !(file instanceof File)) {
        return formData
      }

      const renamedFile = new File([file], `${contractId}/${file.name}`, {
        type: file.type,
        lastModified: file.lastModified,
      })
      const renamedFormData = new FormData()
      renamedFormData.append('File', renamedFile)
      return renamedFormData
    },
    [contractId]
  )

  const onUploadFile = useCallback(
    async (
      authToken: string,
      file: string | FormData,
      uploadCampaignId: string,
      uploadedFormType: DocumentTypes,
      uploadProgress?: (progressEvent: AxiosProgressEvent) => void,
      abortSignal?: GenericAbortSignal,
      uploadContractId?: string
    ) => {
      try {
        const renamedFormData =
          typeof FormData !== 'undefined' && file instanceof FormData
            ? buildRenamedFormData(file)
            : file

        return await uploadFile(
          authToken,
          renamedFormData,
          uploadCampaignId,
          uploadedFormType,
          uploadProgress ?? null,
          abortSignal ?? null,
          undefined,
          uploadContractId
        )
      } catch {
        return undefined
      }
    },
    [buildRenamedFormData]
  )

  useEffect(() => {
    if (!isOpen) {
      setSelectedOption(undefined)
      resetState()
    }
  }, [isOpen, resetState])

  const onExchangeLetterChanged = useCallback(
    ({ value }: { value?: (UploadedDocument | File)[] }) => {
      setExchangeLetterFiles(value ?? [])
    },
    []
  )

  const onSignedContractChanged = useCallback(
    ({ value }: { value?: (UploadedDocument | File)[] }) => {
      setSignedContractFiles(value ?? [])
    },
    []
  )

  const uploadSelectedFile = useCallback(
    async (
      authToken: string,
      uploadCampaignId: string,
      documentType: DocumentTypes,
      selectedFile?: UploadedDocument | File,
      uploadContractId?: string
    ) => {
      if (!selectedFile || !(selectedFile instanceof File)) return selectedFile

      const formData = new FormData()
      formData.append('File', selectedFile)

      return onUploadFile(
        authToken,
        formData,
        uploadCampaignId,
        documentType,
        undefined,
        undefined,
        uploadContractId
      )
    },
    [onUploadFile]
  )

  const onConfirm = useCallback(
    async (selectedCampaignId: string) => {
      const uploadCampaignId = providedCampaignId ?? selectedCampaignId
      if (!uploadCampaignId) return
      if (isSolicitorExchange && exchangeLetterFiles.length === 0) return
      if (isAgentExchange && signedContractFiles.length === 0) return
      if (
        isSolicitorExchange &&
        selectedOption === 'Yes' &&
        signedContractFiles.length === 0
      ) {
        return
      }

      const authToken = await getAuthToken()
      if (!authToken) return

      setIsSubmitting(true)

      try {
        await uploadSelectedFile(
          authToken,
          uploadCampaignId,
          isAgentExchange
            ? DocumentTypes.SignedContract
            : DocumentTypes.ExchangeLetter,
          isAgentExchange ? signedContractFiles[0] : exchangeLetterFiles[0],
          contractId
        )

        if (isSolicitorExchange && selectedOption === 'Yes') {
          await uploadSelectedFile(
            authToken,
            uploadCampaignId,
            DocumentTypes.SignedContract,
            signedContractFiles[0],
            contractId
          )
        }

        await invalidateTasks(uploadCampaignId, contractId)
        closeModal()
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      closeModal,
      contractId,
      exchangeLetterFiles,
      getAuthToken,
      invalidateTasks,
      isAgentExchange,
      isSolicitorExchange,
      providedCampaignId,
      selectedOption,
      signedContractFiles,
      uploadSelectedFile,
    ]
  )

  const isConfirmDisabled =
    (isSolicitorExchange && !selectedOption) ||
    (isSolicitorExchange && exchangeLetterFiles.length === 0) ||
    (isAgentExchange && signedContractFiles.length === 0) ||
    (isSolicitorExchange &&
      selectedOption === 'Yes' &&
      signedContractFiles.length === 0) ||
    isSubmitting

  return {
    agencyId: agencyId || '',
    campaignId: providedCampaignId,
    exchangeLetterFiles,
    getAuthToken,
    isConfirmDisabled,
    isOpen,
    openModal,
    closeModal,
    selectedOption,
    isAgentExchange,
    isSolicitorExchange,
    setIsDialogOpen: setIsOpen,
    setSelectedOption,
    onConfirm,
    onExchangeLetterChanged,
    onSignedContractChanged,
    signedContractFiles,
    onUploadFile,
  }
}
